import * as React from "react"
import { useSearchParams } from "react-router-dom"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { SearchFilters, type SearchFiltersState } from "@/components/gap-devices/search-filters"
import { DevicesTable } from "@/components/gap-devices/devices-table"
import { AddDeviceForm } from "@/components/gap-devices/add-device-form"
import { useToast } from "@/hooks/use-toast"
import {
  useDevicesByClient,
  useCreateDevice,
  useUpdateDevice,
  useDeleteDevice,
  useBulkDeleteDevices,
  useBulkUpdateDevices,
} from "@/hooks/use-gap-devices"
import { toGapDeviceUI, type GapDeviceUI, type CreateGapDeviceRequest, type UpdateGapDeviceRequest } from "@/types/gap-device"
import { getClientInfo } from "@/lib/client-data"
import { useClients } from "@/hooks/use-gap-clients"

export function GapDevicesPage() {
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const [addDeviceOpen, setAddDeviceOpen] = React.useState(false)
  const [newlyCreatedDeviceId, setNewlyCreatedDeviceId] = React.useState<string | null>(null)

  // Initialize filters from URL params
  const [filters, setFilters] = React.useState<SearchFiltersState>(() => {
    const clientsParam = searchParams.get("clients")
    return {
      search: searchParams.get("q") || "",
      clients: clientsParam ? new Set(clientsParam.split(",")) : new Set(),
      venues: new Set(),
      activeOnly: false,
    }
  })

  // Fetch all devices (backend doesn't support filtering by client, so we fetch all and filter client-side)
  const { data: apiDevices, error, isLoading, mutate: refetchDevices } = useDevicesByClient("all")
  const { data: clients } = useClients()

  // Mutations
  const createDevice = useCreateDevice()
  const updateDevice = useUpdateDevice()
  const deleteDevice = useDeleteDevice()
  const bulkDeleteDevices = useBulkDeleteDevices()
  const bulkUpdateDevices = useBulkUpdateDevices()

  const devices: GapDeviceUI[] = React.useMemo(() => {
    if (!apiDevices) return []
    return apiDevices.map((device) => {
      const clientInfo = getClientInfo(device.clientId)
      return toGapDeviceUI(device, clientInfo?.name || "Unknown", clientInfo?.venue || "Unknown")
    })
  }, [apiDevices])

  const handleDeviceCreated = async (data: CreateGapDeviceRequest) => {
    try {
      await createDevice.trigger(data)
      setNewlyCreatedDeviceId(data.deviceId)
      setAddDeviceOpen(false)
      toast({
        variant: "success",
        title: "Device created successfully",
        description: `Device ${data.deviceId} has been added to the system.`,
      })
      refetchDevices()
      setTimeout(() => setNewlyCreatedDeviceId(null), 3000)
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to create device",
        description: err instanceof Error ? err.message : "Please try again.",
      })
    }
  }

  const handleUpdateDevice = async (deviceId: string, data: UpdateGapDeviceRequest) => {
    try {
      await updateDevice.trigger({ deviceId, data })
      refetchDevices()
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to update device",
        description: err instanceof Error ? err.message : "Please try again.",
      })
      throw err
    }
  }

  const handleDeleteDevice = async (deviceId: string) => {
    try {
      await deleteDevice.trigger(deviceId)
      refetchDevices()
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to delete device",
        description: err instanceof Error ? err.message : "Please try again.",
      })
      throw err
    }
  }

  const handleBulkDelete = async (deviceIds: string[]) => {
    try {
      const result = await bulkDeleteDevices.trigger(deviceIds)
      refetchDevices()
      if (result.failed > 0) {
        toast({
          variant: "warning",
          title: `${result.succeeded} devices deleted`,
          description: `${result.failed} devices failed to delete.`,
        })
      }
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to delete devices",
        description: err instanceof Error ? err.message : "Please try again.",
      })
      throw err
    }
  }

  const handleBulkUpdate = async (updates: Array<{ deviceId: string; data: UpdateGapDeviceRequest }>) => {
    try {
      const result = await bulkUpdateDevices.trigger({ updates })
      refetchDevices()

      // Show appropriate notification based on results
      if (result.failed === 0) {
        // All updates succeeded
        toast({
          variant: "success",
          title: `${result.succeeded} device${result.succeeded !== 1 ? 's' : ''} updated`,
          description: "Configuration changes have been applied.",
        })
      } else if (result.succeeded === 0) {
        // All updates failed
        const failedList = result.failedIds && result.failedIds.length > 0 ? (
          <>
            Failed devices:
            {result.failedIds.map(id => (
              <div key={id}>• {id}</div>
            ))}
          </>
        ) : `${result.failed} device${result.failed !== 1 ? 's' : ''} failed to update.`
        toast({
          variant: "error",
          title: "0 devices updated",
          description: failedList,
        })
      } else {
        // Partial success
        const failedList = result.failedIds && result.failedIds.length > 0 ? (
          <>
            {result.failed} device{result.failed !== 1 ? 's' : ''} failed to update:
            {result.failedIds.map(id => (
              <div key={id}>• {id}</div>
            ))}
          </>
        ) : `${result.failed} device${result.failed !== 1 ? 's' : ''} failed to update.`
        toast({
          variant: "warning",
          title: `${result.succeeded} device${result.succeeded !== 1 ? 's' : ''} updated`,
          description: failedList,
        })
      }
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to update devices",
        description: err instanceof Error ? err.message : "Please try again.",
      })
      throw err
    }
  }

  return (
    <>
      <DashboardHeader onAddDevice={() => setAddDeviceOpen(true)} />
      <main className="container mx-auto px-4 py-6 space-y-6">
        <h1 className="text-2xl font-semibold text-foreground mb-2">Devices</h1>
        <SearchFilters onChange={setFilters} clients={clients || []} />
        <DevicesTable
          devices={devices}
          clients={clients || []}
          isLoading={isLoading}
          error={error}
          onRetry={refetchDevices}
          onAddDevice={() => setAddDeviceOpen(true)}
          highlightedDeviceId={newlyCreatedDeviceId}
          filters={filters}
          onUpdateDevice={handleUpdateDevice}
          onDeleteDevice={handleDeleteDevice}
          onBulkDelete={handleBulkDelete}
          onBulkUpdate={handleBulkUpdate}
          isUpdating={updateDevice.isMutating || bulkUpdateDevices.isMutating}
          isDeleting={deleteDevice.isMutating || bulkDeleteDevices.isMutating}
        />
      </main>
      <AddDeviceForm
        open={addDeviceOpen}
        onOpenChange={setAddDeviceOpen}
        onSubmit={handleDeviceCreated}
        isSubmitting={createDevice.isMutating}
        clients={clients || []}
      />
    </>
  )
}
